// Route tester — the pure part. No React, no Google Maps, no fetch: everything
// here is a function of mapData (buildMapData's result) and the shared shapes
// in the contract (Endpoint, Edit, Params, Metrics), so it runs under jsdom
// and in Node alike. RouteTester.jsx owns the screen; this file owns the
// meaning of what it shows.
//
// The one rule that matters: EDITS NEVER TOUCH MAP DATA. An edit is translated
// into findRoute options (`editOptions`) at the moment a route is computed, and
// nothing else — mapData stays exactly what the snapshot said.

import { dayOf } from '../../utils/analytics.js';

/** The four variants, in the order the UI lists them. Names are fixed by the contract. */
export const VARIANTS = [
  { key: 'base', label: 'Normal', short: 'Normal', options: {}, color: '#1a73e8', weight: 6, dash: 'solid' },
  { key: 'stairs', label: 'Stairs only', short: 'Stairs', options: { noElevators: true }, color: '#f29900', weight: 5, dash: 'dashed' },
  { key: 'accessible', label: 'Accessible', short: 'Access.', options: { accessible: true }, color: '#34a853', weight: 5, dash: 'solid' },
  { key: 'weather', label: 'Bad weather (tunnels)', short: 'Weather', options: { tunnels: true }, color: '#8e24aa', weight: 5, dash: 'dotted' },
];
export const VARIANT_BY_KEY = Object.fromEntries(VARIANTS.map((v) => [v.key, v]));
export const TOGGLE_KEYS = ['stairs', 'accessible', 'weather'];

/** Node colours by type — the legend and the markers read the same table. */
export const NODE_COLORS = {
  waypoint: '#9e9e9e',
  door: '#1a73e8',
  intersection: '#607d8b',
  exit: '#e53935',
  stair: '#a0724a',
  elevator: '#78909c',
  parking: '#2e7d32',
  'non-accessible': '#f29900',
  blocked: '#000000',
  shaftOff: '#9e9e9e',
};
export const LEGEND = [
  { key: 'waypoint', label: 'Waypoint' },
  { key: 'door', label: 'Door' },
  { key: 'intersection', label: 'Intersection' },
  { key: 'exit', label: 'Exit' },
  { key: 'stair', label: 'Stair' },
  { key: 'elevator', label: 'Elevator' },
  { key: 'parking', label: 'Parking' },
  { key: 'non-accessible', label: 'Non-accessible', mark: '⚠' },
  { key: 'blocked', label: 'Blocked', mark: '✕' },
  { key: 'shaftOff', label: 'Disabled shaft', hatched: true },
];

export const START_COLOR = '#34a853';
export const END_COLOR = '#e53935';
export const WALK_METERS_PER_MIN = 80;

// ── Edits → routing options ──────────────────────────────────────────────────

/**
 * The contract's mapping, and the only place it is written down in the client:
 *   node … 'blocked'         → excludeNodes
 *   node … 'non-accessible'  → nodeTypes (only bites with `accessible: true`)
 *   shaft                    → excludeEdges gets every floor-to-floor link;
 *                              the nodes stay, so a corridor past the landing
 *                              still works.
 */
export function editOptions(edits) {
  const excludeNodes = new Set();
  const excludeEdges = new Set();
  const nodeTypes = new Map();
  for (const e of edits || []) {
    if (e.kind === 'node') {
      if (e.type === 'blocked') excludeNodes.add(e.id);
      else if (e.type === 'non-accessible') nodeTypes.set(e.id, 'non-accessible');
    } else if (e.kind === 'shaft') {
      for (const id of e.edgeIds || []) excludeEdges.add(id);
    }
  }
  return { excludeNodes, excludeEdges, nodeTypes };
}

/** What a node reads as once the edits are applied: its own type, or the edit's. */
export function effectiveType(node, edits) {
  for (const e of edits || []) {
    if (e.kind === 'node' && e.id === node.id) return e.type;
    if (e.kind === 'shaft' && e.nodeIds?.includes(node.id)) return 'shaftOff';
  }
  return node.type || 'waypoint';
}

// ── Metrics ──────────────────────────────────────────────────────────────────

/** Minutes exactly as SearchPanel.jsx rounds them; nothing added for floors. */
export function minutesOf(meters) {
  return Math.max(1, Math.round(meters / WALK_METERS_PER_MIN));
}

export function metricsOf(route) {
  if (!route) return null;
  return {
    meters: Math.round(route.totalMeters || 0),
    outdoorMeters: Math.round(route.outdoorMeters || 0),
    tunnelMeters: Math.round(route.tunnelMeters || 0),
    floorChanges: (route.transitions || []).length,
    mins: minutesOf(route.totalMeters || 0),
  };
}

/** "412 m · 2 floor changes · 5 min", or "No route". */
export function metricsSummary(m) {
  if (!m) return 'No route';
  const fc = m.floorChanges === 1 ? '1 floor change' : `${m.floorChanges} floor changes`;
  return `${m.meters} m · ${fc} · ${m.mins} min`;
}

// ── Endpoints ────────────────────────────────────────────────────────────────

export function nodeLabel(node) {
  if (!node) return '';
  return node.label || node.room_number || node.id;
}

export function nodeEndpoint(node) {
  return {
    kind: 'node', id: node.id, label: nodeLabel(node),
    ...(node.building ? { building: node.building } : {}),
    ...(Number.isFinite(node.floor) ? { floor: node.floor } : {}),
  };
}

export function roomEndpoint(entry) {
  return { kind: 'room', building: entry.building, room: entry.roomNumber, label: `${entry.buildingName || entry.building} ${entry.roomNumber}` };
}

/** mapData.roomIndex entry for a building + room number, case-insensitive. */
export function roomEntry(mapData, building, room) {
  if (!mapData || !building || room == null) return null;
  const r = String(room).toLowerCase();
  return mapData.roomIndex.find((e) => e.building === building && String(e.roomNumber).toLowerCase() === r) || null;
}

/** The node ids findRoute gets for an endpoint: one for a node, every door for a room. */
export function endpointIds(mapData, ep) {
  if (!mapData || !ep) return [];
  if (ep.kind === 'node') return mapData.nodes.has(ep.id) ? [ep.id] : [];
  if (ep.kind === 'room') return roomEntry(mapData, ep.building, ep.room)?.nodeIds || [];
  return [];
}

export function buildingName(mapData, slug) {
  if (!slug) return '';
  if (slug === 'outdoors') return 'Outdoors';
  if (slug === 'tunnels') return 'Tunnels';
  return mapData?.buildings?.find((b) => b.slug === slug)?.name || slug;
}

export function endpointLabel(mapData, ep) {
  if (!ep) return '';
  if (ep.kind === 'room') return ep.label || `${buildingName(mapData, ep.building)} ${ep.room}`;
  const node = mapData?.nodes?.get(ep.id);
  if (node) {
    const where = node.floor === 0 ? 'outdoors' : `${buildingName(mapData, node.building)} F${node.floor}`;
    return `${nodeLabel(node)} (${node.type}, ${where})`;
  }
  return ep.label || ep.id;
}

export function sameEndpoint(a, b) {
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === 'node') return a.id === b.id;
  return a.building === b.building && String(a.room).toLowerCase() === String(b.room).toLowerCase();
}

/**
 * The /umanitoba "From" rule (PathfinderTab's fromResolved): the longest
 * building name that prefixes the text, then a room number or a room name;
 * failing that an exact "Building Number" label. Null when nothing matches.
 */
export function resolveFromText(mapData, text) {
  if (!mapData) return null;
  const t = String(text || '').trim();
  if (!t) return null;
  const lower = t.toLowerCase();
  let building = null;
  for (const b of mapData.buildings) {
    const bn = (b.name || '').toLowerCase();
    if (!bn) continue;
    if (lower === bn || lower.startsWith(bn + ' ')) {
      if (!building || b.name.length > building.name.length) building = b;
    }
  }
  if (building) {
    const rest = t.slice(building.name.length).trim().toLowerCase();
    if (!rest) return null;
    const entry = mapData.roomIndex.find((r) => r.building === building.slug &&
      (String(r.roomNumber).toLowerCase() === rest || (r.roomName || '').toLowerCase() === rest));
    return entry ? roomEndpoint(entry) : null;
  }
  const entry = mapData.roomIndex.find((r) => `${r.buildingName} ${r.roomNumber}`.toLowerCase() === lower);
  return entry ? roomEndpoint(entry) : null;
}

// ── Query params ─────────────────────────────────────────────────────────────

/**
 * The same four /umanitoba reads (`from`, `b`, `r`, `a`, `t`), plus
 * `suggestion`. `a`/`t` are the tri-states routeLink writes: a=1 accessible,
 * a=2 stairs only, t=1 tunnels. Anything else is no preference.
 */
export function parseQuery(search) {
  try {
    const p = new URLSearchParams(search || '');
    const b = p.get('b') || '', r = p.get('r') || '', from = p.get('from') || '';
    const a = p.get('a'), t = p.get('t');
    const suggestion = p.get('suggestion') || '';
    if (!b && !r && !from && !a && !t && !suggestion) return null;
    return {
      from, b, r, suggestion,
      variants: { stairs: a === '2', accessible: a === '1', weather: t === '1' },
    };
  } catch { return null; }
}

/** The /umanitoba link for the current test, built like shared.js's routeLink. */
export function openInPathfinderLink(mapData, start, end, variants, routeLink) {
  if (!end || end.kind !== 'room') return null;
  const issue = {
    from: start?.kind === 'room' ? endpointLabel(mapData, start) : '',
    to: { building: end.building, room: end.room },
    options: { accessible: !!variants?.accessible, noElevators: !!variants?.stairs, tunnels: !!variants?.weather },
  };
  const url = routeLink(issue);
  if (!url) return null;
  if (start?.kind === 'node') {
    try { const u = new URL(url); u.searchParams.set('n', start.id); return u.toString(); } catch { return url; }
  }
  return url;
}

// ── Edits ────────────────────────────────────────────────────────────────────

export function nodeEdit(node, type) {
  return {
    kind: 'node', id: node.id, type, label: nodeLabel(node),
    ...(node.building ? { building: node.building } : {}),
    ...(Number.isFinite(node.floor) ? { floor: node.floor } : {}),
  };
}

export function shaftEdit(node, shaft) {
  return {
    kind: 'shaft', type: shaft.type, nodeIds: [...shaft.nodeIds], edgeIds: [...shaft.edgeIds],
    label: nodeLabel(node), ...(node.building ? { building: node.building } : {}),
  };
}

export function sameEdit(a, b) {
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === 'node') return a.id === b.id && a.type === b.type;
  const ea = [...(a.edgeIds || [])].sort().join(','), eb = [...(b.edgeIds || [])].sort().join(',');
  return a.type === b.type && ea === eb;
}

/** Does this edit already cover the node (either node edit on it, or its shaft)? */
export function editCovering(edits, nodeId) {
  return (edits || []).findIndex((e) => (e.kind === 'node' && e.id === nodeId) || (e.kind === 'shaft' && e.nodeIds?.includes(nodeId)));
}

/** "Blocked node 12A on floor 2 of Armes" / "Disabled elevator 40el (floors 1–3)". */
export function editLabel(edit, mapData) {
  if (!edit) return '';
  const bname = buildingName(mapData, edit.building);
  if (edit.kind === 'shaft') {
    const floors = shaftFloorsOf(edit, mapData);
    const span = floors.length ? ` (floors ${floors[0]}–${floors[floors.length - 1]})` : '';
    return `Disabled ${edit.type} ${edit.label || edit.nodeIds?.[0] || ''}${span}${bname ? ` in ${bname}` : ''}`;
  }
  const what = edit.type === 'blocked' ? 'Blocked' : 'Non-accessible';
  const where = Number.isFinite(edit.floor)
    ? (edit.floor === 0 ? ' outdoors' : ` on floor ${edit.floor}${bname ? ` of ${bname}` : ''}`)
    : (bname ? ` in ${bname}` : '');
  return `${what} node ${edit.label || edit.id}${where}`;
}

function shaftFloorsOf(edit, mapData) {
  const out = new Set();
  for (const id of edit.nodeIds || []) {
    const f = mapData?.nodes?.get(id)?.floor;
    if (Number.isFinite(f)) out.add(f);
  }
  return [...out].sort((a, b) => a - b);
}

/**
 * The stair/elevator shaft a node belongs to — the chain of same-type nodes
 * joined floor to floor. The routing lib's `shaftOf` is the real one; this is
 * the same walk, kept here so the tester works whichever ships first.
 */
export function localShaftOf(mapData, nodeId) {
  const start = mapData?.nodes?.get(nodeId);
  if (!start || (start.type !== 'stair' && start.type !== 'elevator')) return null;
  const nodeIds = [nodeId];
  const edgeIds = [];
  const seen = new Set([nodeId]);
  const queue = [nodeId];
  while (queue.length) {
    const id = queue.shift();
    const here = mapData.nodes.get(id);
    for (const { edge, other } of mapData.adjacency.get(id) || []) {
      const n = mapData.nodes.get(other);
      if (!n || n.type !== start.type || n.floor === here.floor) continue;
      if (!edgeIds.includes(edge.id)) edgeIds.push(edge.id);
      if (seen.has(other)) continue;
      seen.add(other);
      nodeIds.push(other);
      queue.push(other);
    }
  }
  if (!edgeIds.length) return null;
  const floors = [...new Set(nodeIds.map((id) => mapData.nodes.get(id).floor))].sort((a, b) => a - b);
  return { type: start.type, nodeIds, edgeIds, floors };
}

// ── Floors and drawing ───────────────────────────────────────────────────────

/** Floor 0 (outdoors) always shows; floor −1 (tunnels) shows with floor 1. */
export function visibleOnFloor(node, floor) {
  const f = node?.floor;
  return f === 0 || f == null || f === floor || (f === -1 && floor === 1);
}

/**
 * A route as runs of consecutive nodes that are all on the shown floor
 * (`visible: true`) or not. Each edge is drawn once, dimmed when either end is
 * off-floor — like /umanitoba's inactive legs.
 */
export function splitRuns(route, floor) {
  const nodes = route?.nodes || [];
  const runs = [];
  for (let i = 0; i + 1 < nodes.length; i++) {
    const a = nodes[i], b = nodes[i + 1];
    const vis = visibleOnFloor(a, floor) && visibleOnFloor(b, floor);
    const last = runs[runs.length - 1];
    if (last && last.visible === vis) last.path.push({ lat: b.lat, lng: b.lng });
    else runs.push({ visible: vis, path: [{ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng }] });
  }
  return runs;
}

// ── History ──────────────────────────────────────────────────────────────────

/** Newest first, grouped by local calendar day with a readable heading. */
export function groupByDay(entries) {
  const groups = [];
  const byDay = new Map();
  for (const e of [...(entries || [])].sort((a, b) => (b.at || 0) - (a.at || 0))) {
    const day = dayOf(e.at || 0);
    let g = byDay.get(day);
    if (!g) { g = { day, label: dayLabel(day), entries: [] }; byDay.set(day, g); groups.push(g); }
    g.entries.push(e);
  }
  return groups;
}

function dayLabel(day) {
  const today = dayOf(new Date());
  const yest = dayOf(new Date(Date.now() - 86400000));
  if (day === today) return 'Today';
  if (day === yest) return 'Yesterday';
  const [y, m, d] = day.split('-').map(Number);
  try {
    return new Date(y, m - 1, d, 12).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: y !== new Date().getFullYear() ? 'numeric' : undefined });
  } catch { return day; }
}

export function variantsText(variants) {
  const on = TOGGLE_KEYS.filter((k) => variants?.[k]).map((k) => VARIANT_BY_KEY[k].label);
  return on.length ? on.join(' · ') : 'Normal';
}

export function isTypingTarget(el) {
  if (!el) return false;
  const tag = String(el.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || !!el.isContentEditable;
}
